import { BlockSections } from '@/components/builder/inspector-sections';
import { StepSection } from '@/components/builder/step-section';
import { stepBounds } from '@/components/builder/validate';
import { cn } from '@/lib/cn';
import { findNode, findStep, type SessionStore } from '@personalizehq/editor';
import { MAX_LAYOUT_DEPTH, MAX_LAYOUT_NODES } from '@personalizehq/schemas';
import type { BlockNode } from '@personalizehq/schemas/ir';
import { X } from 'lucide-react';
import { useStore } from 'zustand';

// The inspector is now CHROME AND A MAP. Which sections a block gets, and in what order, is a table
// in `inspector-sections.tsx`; the widgets are in `inspector-controls.tsx`. This file used to be
// 1145 lines because all three lived in it.
//
// Editing is not through setters: `applyChange(draft => { node.gap = 8 })` under immer is smaller
// than any setter API and keeps the discriminated union's type safety. The `mergeKey`
// (`${id}.${field}`) is what makes ⌘Z undo a word rather than a letter.

export function Inspector({ session }: { session: SessionStore }): React.JSX.Element {
  const doc = useStore(session, (s) => s.doc);
  const selection = useStore(session, (s) => s.selection);
  const focusedStepId = useStore(session, (s) => s.focusedStepId);

  const step = findStep(doc, focusedStepId);
  const hit = step && selection !== null ? findNode(step.root, selection) : undefined;
  const bounds = stepBounds(doc, focusedStepId);
  // PARALLAX IS SCROLL-DRIVEN (SPEC §5.0e) and publish rejects it without a scroll above — a law
  // that cannot be seen from the node, only from the tree. So the field is not OFFERED without one:
  // an inspector that lets you author an unpublishable document is an inspector that lies.
  const inScroll = step && selection !== null ? hasScrollAncestor(step.root, selection) : false;

  const set = <T extends BlockNode>(field: string, mutate: (node: T) => void): void => {
    session.getState().applyChange((draft) => {
      const target = findNode(findStep(draft, focusedStepId)!.root, selection!);
      if (target) mutate(target.node as T);
    }, `${selection}.${field}`);
  };

  return (
    <div className="flex h-full flex-col">
      {hit ? (
        <>
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="font-medium">{hit.node.block}</span>
            <button
              type="button"
              aria-label="Clear selection"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => session.getState().select(null)}
            >
              <X className="size-3.5" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <BlockSections
              node={hit.node}
              parent={hit.parent}
              root={step?.root ?? null}
              // The ROOT's height law is the SURFACE's (SPEC §6.1c) — a `fill` root fills the
              // viewport, and only a fullscreen step has one to fill.
              step={step ? { rootId: step.root.id, surface: step.surface, theme: doc.theme } : undefined}
              theme={doc.theme}
              set={set}
              inScroll={inScroll}
            />
          </div>
        </>
      ) : (
        // NOT a dead end any more. With nothing selected, the thing you are looking at is the STEP —
        // so this is where its name, its surface and its auto-advance live (B3). `surface` had
        // existed in the schema since P3 with no control anywhere, which is how `inline` — a v1
        // requirement — stayed unauthorable.
        <div className="min-h-0 flex-1 overflow-y-auto">
          <StepSection session={session} />
        </div>
      )}

      {/* The bounds are the publish gate's, shown as a meter rather than discovered as a rejection. */}
      <div
        className={cn(
          'border-t px-3 py-2 text-[11px]',
          bounds.over ? 'text-destructive' : bounds.near ? 'text-amber-500' : 'text-muted-foreground'
        )}
      >
        {bounds.nodes}/{MAX_LAYOUT_NODES} nodes · depth {bounds.depth}/{MAX_LAYOUT_DEPTH}
      </div>
    </div>
  );
}

/** Is there a `scroll` ABOVE this node? Parallax translates against the nearest ancestor scroll's
 *  offset (SPEC §5.0e), so without one the field would do nothing, forever. */
function hasScrollAncestor(root: BlockNode, id: string): boolean {
  const walk = (n: BlockNode, seen: boolean): boolean => {
    if (n.id === id) return seen;
    if (!('children' in n)) return false;
    const next = seen || n.block === 'scroll';
    return n.children.some((c) => walk(c, next));
  };
  return walk(root, false);
}
